import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import {
  Loader2, CheckCircle2, AlertCircle, RefreshCw, Trash2, Eye, EyeOff,
  ShieldCheck, Clock, ArrowRight, Plug,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  app_key: '',
  app_secret: '',
  access_token: '',
  restaurant_id: '',
};

/**
 * Inline Petpooja connect panel — drop into any page that has a `client`.
 * Use this directly inside the POS Integrations page. Self-contained: handles
 * its own load / save / test / sync / disconnect / history.
 */
export const PetpoojaConnectPanel = ({ client, isAdmin, compact = false }) => {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(false);
  const [showSecrets, setShowSecrets] = useState(false);
  const [busy, setBusy] = useState({ load: false, save: false, test: false, sync: false, del: false });
  const [testResult, setTestResult] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState(null);

  const auth = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('access_token') || ''}` },
  });

  const loadAll = useCallback(async () => {
    if (!client?.id) return;
    setBusy(b => ({ ...b, load: true })); setError(null);
    try {
      const [st, lg] = await Promise.all([
        axios.get(`${API}/clients/${client.id}/integrations/petpooja`, auth()),
        axios.get(`${API}/clients/${client.id}/integrations/petpooja/logs?limit=8`, auth()),
      ]);
      setStatus(st.data);
      setLogs(lg.data?.logs || []);
      setEditing(!st.data?.connected);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(b => ({ ...b, load: false }));
    }
  }, [client?.id]);

  useEffect(() => {
    setTestResult(null); setSyncResult(null); setError(null); setForm(EMPTY_FORM);
    loadAll();
  }, [loadAll]);

  const save = async (e) => {
    e?.preventDefault();
    setBusy(b => ({ ...b, save: true })); setError(null); setTestResult(null);
    try {
      const { data } = await axios.put(
        `${API}/clients/${client.id}/integrations/petpooja`, form, auth(),
      );
      setStatus(data); setForm(EMPTY_FORM); setEditing(false);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(b => ({ ...b, save: false }));
    }
  };

  const testConn = async () => {
    setBusy(b => ({ ...b, test: true })); setTestResult(null);
    try {
      const { data } = await axios.post(
        `${API}/clients/${client.id}/integrations/petpooja/test`, {}, auth(),
      );
      setTestResult(data);
    } catch (e) {
      setTestResult({ ok: false, message: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(b => ({ ...b, test: false }));
    }
  };

  const syncNow = async () => {
    setBusy(b => ({ ...b, sync: true })); setSyncResult(null);
    try {
      const { data } = await axios.post(
        `${API}/clients/${client.id}/integrations/petpooja/sync`, {}, auth(),
      );
      setSyncResult(data);
      await loadAll();
    } catch (e) {
      setSyncResult({ ok: false, error: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(b => ({ ...b, sync: false }));
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Petpooja for this client? Credentials will be permanently deleted.')) return;
    setBusy(b => ({ ...b, del: true })); setError(null);
    try {
      await axios.delete(`${API}/clients/${client.id}/integrations/petpooja`, auth());
      await loadAll();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(b => ({ ...b, del: false }));
    }
  };

  const fieldClass = "w-full border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 font-mono";

  if (busy.load) {
    return (
      <div className="flex items-center gap-2 text-sm text-text-muted py-6 justify-center" data-testid="petpooja-panel-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading Petpooja integration state…
      </div>
    );
  }
  if (!status) return null;

  return (
    <div className="space-y-4" data-testid="petpooja-connect-panel">
      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-background-secondary rounded-md px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-orange-100 text-orange-700">
            <Plug className="h-5 w-5" />
          </div>
          <div>
            <div className="text-sm font-semibold text-text-primary">Petpooja API connection</div>
            <div className="text-xs text-text-secondary">
              Encrypted at rest · Auto-sync nightly at 3 AM IST
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {status.connected ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-state-success bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1"
                  data-testid="petpooja-status-connected">
              <CheckCircle2 className="h-3.5 w-3.5" /> Connected
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-text-muted bg-zinc-100 border border-border rounded-full px-2.5 py-1"
                  data-testid="petpooja-status-disconnected">
              <AlertCircle className="h-3.5 w-3.5" /> Not connected
            </span>
          )}
        </div>
      </div>

      {status.connected && !editing && (
        <>
          {/* Identity strip + last sync */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
            <InfoTile label="Restaurant ID" value={status.restaurant_id_hint || '—'} mono />
            <InfoTile label="App Key" value={status.app_key_hint || '—'} mono />
            <InfoTile
              label="Last sync"
              value={status.last_sync_at ? new Date(status.last_sync_at).toLocaleString() : 'Never'}
              badge={status.last_sync_status}
            />
          </div>

          {/* Per-module summary */}
          {status.last_sync_summary && !compact && (
            <div className="border border-border rounded-md p-3 text-xs space-y-1.5 bg-white">
              <div className="font-semibold text-text-primary mb-1">Last sync result by module</div>
              {Object.entries(status.last_sync_summary).map(([mod, r]) => (
                <div key={mod} className="flex items-center justify-between" data-testid={`module-row-${mod}`}>
                  <div className="flex items-center gap-2">
                    {r.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-state-success" />
                           : <AlertCircle className="h-3.5 w-3.5 text-state-error" />}
                    <span className="capitalize font-medium">{mod}</span>
                  </div>
                  <div className="text-text-secondary">{r.rows} rows · {r.message}</div>
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          {isAdmin && (
            <div className="flex flex-wrap gap-2">
              <Button onClick={syncNow} disabled={busy.sync} variant="outline" data-testid="petpooja-sync-button">
                {busy.sync ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-2" />}
                Sync now
              </Button>
              <Button onClick={testConn} disabled={busy.test} variant="outline" data-testid="petpooja-test-button">
                {busy.test ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5 mr-2" />}
                Test connection
              </Button>
              <Button onClick={() => setEditing(true)} variant="outline" data-testid="petpooja-update-creds-button">
                Update credentials
              </Button>
              <Button onClick={disconnect} disabled={busy.del} variant="outline"
                      className="text-state-error border-red-200 hover:bg-red-50"
                      data-testid="petpooja-disconnect-button">
                {busy.del ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 mr-2" />}
                Disconnect
              </Button>
            </div>
          )}

          {testResult && (
            <div className={`text-xs rounded-md p-3 border ${testResult.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-red-50 border-red-200 text-red-900'}`}
                 data-testid="petpooja-test-result">
              <div className="font-semibold mb-0.5 flex items-center gap-1.5">
                {testResult.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                {testResult.ok ? 'Connection successful' : 'Connection failed'}
              </div>
              <div className="opacity-80">{testResult.message}</div>
            </div>
          )}

          {syncResult && (
            <div className={`text-xs rounded-md p-3 border ${syncResult.ok ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}
                 data-testid="petpooja-sync-result">
              <div className="font-semibold mb-1">
                {syncResult.ok ? 'Sync complete' : 'Sync finished with errors'}
                {syncResult.duration_sec && <span className="ml-2 text-text-secondary font-normal">({syncResult.duration_sec}s)</span>}
              </div>
              {syncResult.modules && (
                <div className="space-y-0.5">
                  {Object.entries(syncResult.modules).map(([m, r]) => (
                    <div key={m} className="flex items-center gap-2">
                      {r.ok ? '✓' : '✗'} <span className="capitalize">{m}</span> — {r.message}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {logs.length > 0 && !compact && (
            <div className="text-xs">
              <div className="font-semibold mb-1 text-text-secondary">Recent sync history</div>
              <div className="border border-border rounded-md divide-y divide-border bg-white">
                {logs.slice(0, 5).map((l, i) => (
                  <div key={l.id || i} className="px-3 py-1.5 flex items-center justify-between"
                       data-testid={`log-row-${i}`}>
                    <div className="flex items-center gap-2">
                      {l.ok ? <CheckCircle2 className="h-3 w-3 text-state-success" />
                             : <AlertCircle className="h-3 w-3 text-state-error" />}
                      <span className="text-text-secondary">{new Date(l.finished_at).toLocaleString()}</span>
                    </div>
                    <span className="text-text-muted">{l.triggered_by} · {l.duration_sec}s</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {(editing || !status.connected) && isAdmin && (
        <form onSubmit={save} className="space-y-3 bg-white border border-border rounded-md p-4" data-testid="petpooja-creds-form">
          <div className="text-xs text-text-secondary bg-amber-50 border border-amber-200 rounded p-2">
            <strong>Where do these come from?</strong> Petpooja issues these to approved integration
            partners only. Email <span className="font-mono">api@petpooja.com</span> with your
            partner request. All four fields are required and stored AES-256-GCM encrypted.
          </div>

          <div className="flex items-center justify-between">
            <label className="text-sm font-medium">Enter API credentials</label>
            <button type="button"
                    onClick={() => setShowSecrets(s => !s)}
                    className="text-xs text-text-muted hover:text-primary flex items-center gap-1"
                    data-testid="toggle-secrets">
              {showSecrets ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              {showSecrets ? 'Hide' : 'Show'} values
            </button>
          </div>

          {[
            ['restaurant_id', 'Restaurant ID', 'e.g. 1234567'],
            ['app_key', 'App Key', 'app_key from Petpooja'],
            ['app_secret', 'App Secret', 'app_secret from Petpooja'],
            ['access_token', 'Access Token', 'access_token from Petpooja'],
          ].map(([key, label, placeholder]) => (
            <div key={key}>
              <label className="block text-xs text-text-secondary mb-1">{label}</label>
              <input
                type={key === 'restaurant_id' || showSecrets ? 'text' : 'password'}
                value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                className={fieldClass}
                placeholder={placeholder}
                required
                data-testid={`petpooja-${key.replace('_', '-')}-input`}
              />
            </div>
          ))}

          {error && (
            <div className="text-xs text-state-error bg-red-50 border border-red-200 rounded p-2"
                 data-testid="petpooja-error">{error}</div>
          )}

          <div className="flex gap-2 pt-1">
            <Button type="submit" disabled={busy.save}
                    className="bg-primary hover:bg-primary-hover"
                    data-testid="petpooja-save-button">
              {busy.save ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-2" />}
              {status.connected ? 'Update credentials' : 'Connect Petpooja'}
            </Button>
            {status.connected && (
              <Button type="button" variant="outline" onClick={() => { setEditing(false); setError(null); }}>
                Cancel
              </Button>
            )}
          </div>
        </form>
      )}

      {!isAdmin && !status.connected && (
        <div className="text-xs text-text-secondary bg-zinc-50 border border-border rounded p-3">
          Only admin users can configure Petpooja integration. Ask an admin to connect this client.
        </div>
      )}
    </div>
  );
};

const InfoTile = ({ label, value, mono, badge }) => (
  <div className="bg-white border border-border rounded-md p-2.5">
    <div className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">{label}</div>
    <div className={`text-sm text-text-primary mt-0.5 ${mono ? 'font-mono' : ''}`}>{value}</div>
    {badge && (
      <div className={`text-[10px] mt-1 font-semibold ${
        badge === 'success' ? 'text-state-success' : 'text-state-warning'
      }`}>
        {badge}
      </div>
    )}
  </div>
);

export default PetpoojaConnectPanel;
